import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IoArrowBack, IoShareSocialOutline, IoTrashOutline, IoCopyOutline, IoDownloadOutline, IoChevronForward } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import ShareContent from "@/components/ShareContent";
import { listSharesWithMe, listMyShares, markSharesSeen, getShare, revokeShare } from "@/lib/api";

const PERM_LABEL = { view: "Lecture", comment: "Commentaire", edit: "Modification" };

export default function Partages() {
  const [tab, setTab] = useState("recus");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [open, setOpen] = useState(null); // {share, content}

  const load = () => {
    listSharesWithMe().then(setReceived).catch(() => {});
    listMyShares().then(setSent).catch(() => {});
  };
  useEffect(() => { load(); markSharesSeen().catch(() => {}); }, []);

  const openShare = async (id) => {
    try { const d = await getShare(id); setOpen(d); } catch { toast.error("Partage introuvable"); }
  };
  const reload = async () => { if (open) { try { setOpen(await getShare(open.share.id)); } catch {} } };
  const revoke = async (id) => {
    if (!window.confirm("Arrêter ce partage ?")) return;
    try { await revokeShare(id); toast.success("Partage arrêté"); load(); } catch { toast.error("Échec"); }
  };
  const copyLink = (token) => {
    const url = `${window.location.origin}/partage/${token}`;
    navigator.clipboard?.writeText(url); toast.success("Lien copié");
  };

  if (open) {
    return (
      <DojoShell crumbs={[{ label: "Partages", to: "/carnet/partages" }, { label: open.share.title || "Partage" }]}>
        <button style={s.back} onClick={() => setOpen(null)} data-testid="share-view-back"><IoArrowBack size={16} /> Retour aux partages</button>
        <ShareContent share={open.share} content={open.content} onReload={reload} />
      </DojoShell>
    );
  }

  return (
    <DojoShell crumbs={[{ label: "Partages" }]}>
      <div style={s.hero}>
        <div style={s.eyebrow}><IoShareSocialOutline /> ESPACE PARTAGE</div>
        <h1 style={s.h1}>Partages</h1>
        <p style={s.sub}>Les documents et dossiers que l'on t'a partagés, et ceux que tu partages.</p>
      </div>

      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(tab === "recus" ? s.tabOn : {}) }} onClick={() => setTab("recus")} data-testid="share-tab-received">Reçus ({received.length})</button>
        <button style={{ ...s.tab, ...(tab === "envoyes" ? s.tabOn : {}) }} onClick={() => setTab("envoyes")} data-testid="share-tab-sent">Mes partages ({sent.length})</button>
      </div>

      {tab === "recus" ? (
        received.length === 0 ? <div style={s.empty} data-testid="share-received-empty">Personne ne t'a encore rien partagé.</div> : (
          <div style={s.list} data-testid="share-received-list">
            {received.map((sh) => (
              <button key={sh.id} style={s.row} onClick={() => openShare(sh.id)} data-testid={`share-received-${sh.id}`}>
                <span style={s.rowIcon}>{sh.target_type === "folder" ? "📁" : "📄"}</span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={s.rowTitle}>{sh.title || "Partage"}</div>
                  <div style={s.rowSub}>De {sh.owner_name} · {PERM_LABEL[sh.permission]}</div>
                </div>
                <IoChevronForward size={16} style={{ color: "var(--text-secondary)" }} />
              </button>
            ))}
          </div>
        )
      ) : (
        sent.length === 0 ? <div style={s.empty} data-testid="share-sent-empty">Tu n'as encore rien partagé. Utilise le bouton « Partager » dans ton Carnet.</div> : (
          <div style={s.list} data-testid="share-sent-list">
            {sent.map((sh) => (
              <div key={sh.id} style={s.row} data-testid={`share-sent-${sh.id}`}>
                <span style={s.rowIcon}>{sh.target_type === "folder" ? "📁" : "📄"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.rowTitle}>{sh.title || "Partage"}</div>
                  <div style={s.rowSub}>{sh.mode === "link" ? "Lien de partage" : `À ${sh.member_name || "un membre"}`} · {PERM_LABEL[sh.permission]}</div>
                </div>
                {sh.mode === "link" && sh.token && (
                  <button style={s.miniBtn} title="Copier le lien" onClick={() => copyLink(sh.token)} data-testid={`share-copy-${sh.id}`}><IoCopyOutline size={15} /></button>
                )}
                <button style={{ ...s.miniBtn, color: RED }} title="Arrêter" onClick={() => revoke(sh.id)} data-testid={`share-revoke-${sh.id}`}><IoTrashOutline size={15} /></button>
              </div>
            ))}
          </div>
        )
      )}
    </DojoShell>
  );
}

const s = {
  hero: { marginBottom: 20 },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800, letterSpacing: 2, color: RED },
  h1: { fontSize: 38, fontWeight: 900, margin: "8px 0 6px", fontFamily: "var(--font-display)", color: "var(--text-primary)" },
  sub: { fontSize: 15, color: "var(--text-secondary)", margin: 0 },
  tabs: { display: "flex", gap: 8, marginBottom: 18 },
  tab: { padding: "10px 18px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  tabOn: { background: RED, color: "#fff", borderColor: RED },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", cursor: "pointer", width: "100%", boxShadow: "var(--surface-shadow)" },
  rowIcon: { fontSize: 24, flexShrink: 0 },
  rowTitle: { fontWeight: 800, fontSize: 15.5, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: { fontSize: 12.5, color: "var(--text-secondary)" },
  miniBtn: { width: 34, height: 34, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 9, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", cursor: "pointer" },
  empty: { padding: "50px 20px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 16 },
  back: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 18 },
};
